import { apiGet } from '../api.ts'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { QueryCacheKeys } from '@/api/queryKeys.ts'
import { createUrl } from '@/util/url.ts'
import { MINUTE } from '@/constant/time.ts'
import { IResUserRecentState } from '@/api/user/getUserRecentState.ts'
import { IPagingReq, IPagingRes } from '../paging.ts'

interface IReqGetUsers extends IPagingReq {
}

export const apiGetUsersPaging = async (params: IReqGetUsers, query?: string) => {
    return await apiGet<IPagingRes<IUser>>(
        createUrl(`/protectee`, {
            query: query,
            pageNum: params?.pageNum,
            pageSize: params?.pageSize
        })
    )
}

export const apiGetUsers = async () => {
    return await apiGet<IUser[]>(
        createUrl(`/protectee`)
    )
}

// 페이징 파라미터가 있는 경우
function useQueryGetUsers(params: IReqGetUsers & { pageNum: number; pageSize: number }, query?: string): ReturnType<typeof useQuery<IPagingRes<IUser>>>

// 페이징 파라미터가 없는 경우  
function useQueryGetUsers(params: Omit<IReqGetUsers, 'pageNum' | 'pageSize'>, query?: string): ReturnType<typeof useQuery<IUser[]>>

// 실제 구현
function useQueryGetUsers(params: IReqGetUsers, query?: string) {
    return useQuery({
        queryKey: [...QueryCacheKeys.user.getUsers(query), params.pageNum, params.pageSize],
        refetchInterval: 1 * MINUTE,
        queryFn: async () => {
            // 페이징 파라미터가 있으면 apiGetUsersPaging 사용, 없으면 apiGetUsers 사용
            if (params.pageNum || params.pageSize) {
                const { data } = await apiGetUsersPaging(params, query)
                return data
            } else {
                const { data } = await apiGetUsers()
                return data
            }
        },
        placeholderData: keepPreviousData
    })
}

export default useQueryGetUsers

export interface IUser {
    id: number
    centerId: number
    name: string
    gender: number
    birthDate: string
    alertStatus?: AlertStatus
    primaryRelation: string
    primaryContact: string
    secondaryRelation: string
    tertiaryRelation: string
    secondaryContact: string
    tertiaryContact: string
    contact?: string
    inspectorMemo: string
    createdAt: string
    location?: IPosition
    device: IDevice
    profileUrl?: string
    address?: string
    addressLatitude?: number
    addressLongitude?: number
    recentState?: IResUserRecentState
}

export interface IPosition {
    latitude: number
    longitude: number
    timestamp: string
    address: string
    accuracy: number
}

export interface IDevice {
    id: number
    device_serial: string
    name?: any
    last_update: string
    status?: DeviceStatus
    battery?: number
}

export enum DeviceStatus {
    ONLINE = 'online',
    OFFLINE = 'offline',
    CHARGING = 'charging',
    EMERGENCY = 'emergency'
}

export enum AlertStatus {
    NONE = 'none',
    EMERGENCY = 'emergency',
    BIO = 'bio'
}
